/**
 * <s-rail> — horizontal rail. Optional previous/next buttons scroll by one card.
 * With the `autoplay` attribute the rail drifts slowly and loops endlessly (cards are cloned once,
 * hidden from assistive tech), pauses on hover, focus and touch, and can be dragged with the mouse.
 * Reduced motion: no drift, dragging still works. Native scrolling (touch, trackpad, keyboard) keeps working.
 */
class SRail extends HTMLElement {
  #abort;
  #frame = 0;

  connectedCallback() {
    const track = this.querySelector('[data-rail-track]');
    const prev = this.querySelector('[data-rail-prev]');
    const next = this.querySelector('[data-rail-next]');
    if (!track) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = getComputedStyle(this).direction === 'rtl';

    const stepSize = () => {
      const card = track.firstElementChild;
      return card ? card.getBoundingClientRect().width : track.clientWidth * 0.8;
    };
    const dir = () => (rtl ? -1 : 1);
    const sync = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      const pos = Math.abs(track.scrollLeft);
      if (prev) prev.disabled = pos <= 2;
      if (next) next.disabled = pos >= max;
    };
    const behavior = reduce ? 'auto' : 'smooth';
    prev?.addEventListener('click', () => track.scrollBy({ left: -stepSize() * dir(), behavior }), { signal });
    next?.addEventListener('click', () => track.scrollBy({ left: stepSize() * dir(), behavior }), { signal });
    track.addEventListener('scroll', sync, { signal, passive: true });
    window.addEventListener('resize', sync, { signal });
    sync();

    if (this.hasAttribute('autoplay')) this.#setupAuto(track, signal, reduce || rtl);
  }

  #setupAuto(track, signal, still) {
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    const cards = [...track.children];
    let loopWidth = 0;
    if (!still && cards.length > 1) {
      for (const card of cards) {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        track.append(clone);
      }
    }
    const measure = () => {
      if (still || cards.length < 2) return;
      loopWidth = track.children[cards.length].offsetLeft - cards[0].offsetLeft;
    };
    measure();
    window.addEventListener('resize', measure, { signal });

    const wrap = (value) => {
      if (!loopWidth) return value;
      if (value >= loopWidth) return value - loopWidth;
      if (value < 0) return value + loopWidth;
      return value;
    };

    // Mouse drag; touch and trackpad keep native scrolling.
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
    }, { signal });
    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = wrap(startScroll - dx);
      if (loopWidth && (startScroll - dx >= loopWidth || startScroll - dx < 0)) {
        startScroll = track.scrollLeft;
        startX = event.clientX;
      }
    }, { signal });
    window.addEventListener('pointerup', () => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
    }, { signal });
    track.addEventListener('click', (event) => {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, { signal, capture: true });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    if (still || !loopWidth) return;

    let hovering = false;
    let touchUntil = 0;
    let visible = false;
    track.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') hovering = true; }, { signal });
    track.addEventListener('pointerleave', () => { hovering = false; }, { signal });
    track.addEventListener('touchstart', () => { touchUntil = Infinity; }, { signal, passive: true });
    track.addEventListener('touchend', () => { touchUntil = performance.now() + 2500; }, { signal, passive: true });
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; });
    observer.observe(track);
    signal.addEventListener('abort', () => observer.disconnect());

    const speed = 28; // px per second
    let pos = track.scrollLeft;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(now - last, 64) / 1000;
      last = now;
      const paused = dragging || hovering || now < touchUntil || !visible || document.hidden || track.matches(':focus-within');
      if (paused) {
        pos = track.scrollLeft;
      } else {
        if (Math.abs(track.scrollLeft - pos) > 2) pos = track.scrollLeft;
        pos = wrap(pos + speed * dt);
        track.scrollLeft = pos;
      }
      this.#frame = requestAnimationFrame(tick);
    };
    this.#frame = requestAnimationFrame(tick);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.#abort?.abort();
  }
}

if (!customElements.get('s-rail')) customElements.define('s-rail', SRail);
